import Point from 'ol/geom/Point';
import { PixelIntervals } from '../intervals/PixelIntervals.js';
import { PixelFormatter } from '../formatters/PixelFormatter.js';
import { buildStraightGridLine, isOnMajorLine } from '../util/gridlines.js';
import { parsePairViaFormatter } from '../util/parseCoordinatePair.js';
const MAJOR_SKIP_EPSILON_RATIO = 0.01;
function makeYMapping(yInverted) {
    if (yInverted) {
        return {
            toDisplayY: (y) => -y,
            clampOLRange: (min, max) => {
                if (min > 0)
                    return null;
                return [min, Math.min(0, max)];
            },
        };
    }
    return {
        toDisplayY: (y) => y,
        clampOLRange: (min, max) => {
            if (max < 0)
                return null;
            return [Math.max(0, min), max];
        },
    };
}
export class PixelGridSystem {
    intervals_;
    formatter_;
    yMap_;
    constructor(options) {
        this.yMap_ = makeYMapping(options?.yInverted ?? false);
        this.intervals_ =
            options?.intervals ?? new PixelIntervals(options?.targetScreenPx ?? 120);
        this.formatter_ = options?.formatter ?? new PixelFormatter();
    }
    getFeatures(extent, resolution, _viewProjection) {
        const features = [];
        const interval = this.intervals_.getInterval(resolution);
        const minorInterval = this.intervals_.getMinorInterval?.(interval);
        this.generateLines_(features, extent, interval, 'major');
        if (minorInterval !== undefined) {
            this.generateLines_(features, extent, minorInterval, 'minor', interval);
        }
        return features;
    }
    generateLines_(features, extent, interval, type, majorInterval) {
        const [minX, minY, maxX, maxY] = extent;
        const epsilon = interval * MAJOR_SKIP_EPSILON_RATIO;
        const skipMinor = (v) => type === 'minor' &&
            majorInterval !== undefined &&
            isOnMajorLine(v, majorInterval, epsilon);
        const startX = Math.max(0, Math.floor(minX / interval) * interval);
        for (let x = startX; x <= maxX; x += interval) {
            if (skipMinor(x))
                continue;
            features.push(buildStraightGridLine(x, minY, maxY, 'x', type));
        }
        const rawStartY = Math.floor(minY / interval) * interval;
        const yRange = this.yMap_.clampOLRange(rawStartY, maxY);
        if (yRange) {
            const [startY, endY] = yRange;
            for (let y = startY; y <= endY; y += interval) {
                if (skipMinor(y))
                    continue;
                features.push(buildStraightGridLine(y, minX, maxX, 'y', type));
            }
        }
    }
    getLabels(extent, resolution, _viewProjection) {
        const labels = [];
        const interval = this.intervals_.getInterval(resolution);
        const [minX, minY, maxX, maxY] = extent;
        const startX = Math.floor(minX / interval) * interval;
        for (let x = startX; x <= maxX; x += interval) {
            if (x < 0)
                continue;
            labels.push({
                point: new Point([x, maxY]),
                text: this.formatter_.format(x, 'x'),
                axis: 'x',
            });
        }
        const rawStartY = Math.floor(minY / interval) * interval;
        const yRange = this.yMap_.clampOLRange(rawStartY, maxY);
        if (yRange) {
            const [startY, endY] = yRange;
            for (let y = startY; y <= endY; y += interval) {
                labels.push({
                    point: new Point([minX, y]),
                    text: this.formatter_.format(this.yMap_.toDisplayY(y), 'y'),
                    axis: 'y',
                });
            }
        }
        return labels;
    }
    formatCoordinate(coordinate, _viewProjection) {
        const [x, y] = coordinate;
        return {
            x: this.formatter_.format(x, 'x'),
            y: this.formatter_.format(this.yMap_.toDisplayY(y), 'y'),
        };
    }
    parseCoordinate(text, _viewProjection) {
        const [x, displayY] = parsePairViaFormatter(this.formatter_, text);
        return [x, this.yMap_.toDisplayY(displayY)];
    }
}
//# sourceMappingURL=PixelGridSystem.js.map